import { useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import AutomationFilters from "@components/forms/AutomationFilters";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import Nbsp from "@components/Nbsp";
import Table, { TableHeader } from "@components/Table";
import TagList from "@components/TagList";
import Icon from "@components/text/Icon";
import InternalLink from "@components/typography/InternalLink";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  AutomationDestroyDocument,
  AutomationDestroyMutation,
  AutomationDestroyMutationVariables,
  AutomationFragment,
  AutomationSortByEnum,
  GetAutomationsDocument,
  GetAutomationsQuery,
  GetAutomationsQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";

type Filters = ComponentProps<typeof AutomationFilters>["filters"];

const AutomationsTable = () => {
  const intl = useIntl();
  const destroyOpener = useOpener();
  const myExpert = useMyExpert();

  const [getAutomations, getAutomationsQuery] = useLazyQuery<GetAutomationsQuery, GetAutomationsQueryVariables>(
    GetAutomationsDocument
  );

  const [filters, setFilters] = useState<Filters>({
    searchTerm: ""
  });

  const [sortBy, setSortBy] = useState<AutomationSortByEnum>(AutomationSortByEnum.CREATED_AT);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);
  const [destroyingAutomation, setDestroyingAutomation] = useState<AutomationFragment | undefined>(undefined);

  const [destroyAutomation, client] = useMutationWithValidation<
    AutomationDestroyMutation,
    AutomationDestroyMutationVariables
  >(AutomationDestroyDocument, "automation", {
    update: () => {
      // This kicks out all of the automations queries so we will re-make them. If you're flipping between filters
      // deleting will update the automation, but switching filters will have the cached value
      client.cache.evict({ fieldName: "automations", id: "ROOT_QUERY" });
      getAutomationsQuery.refetch();
    }
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const handleAutomationDestroy = useCallback(
    (automation: AutomationFragment) => () => {
      setDestroyingAutomation(automation);
      destroyOpener.open();
    },
    [destroyOpener]
  );

  const handleAutomationDestroyed = useCallback(() => {
    if (destroyingAutomation) {
      destroyAutomation({
        id: destroyingAutomation.id
      });
    }
  }, [destroyingAutomation, destroyAutomation]);

  useEffect(() => {
    getAutomations({
      variables: {
        searchTerm: filters.searchTerm,
        sortBy,
        sortDirection
      }
    });
  }, [filters, getAutomations, sortBy, sortDirection]);

  const automations = useNodesFromConnection(getAutomationsQuery.data?.automations);

  const tableHeaders: TableHeader<AutomationSortByEnum>[] = [
    { columnSortBy: AutomationSortByEnum.TITLE },
    { messageKey: "pages.admin.automations.tableHeadings.tags" },
    { columnSortBy: AutomationSortByEnum.CREATED_AT },
    { columnSortBy: AutomationSortByEnum.UPDATED_AT },
    { columnSortBy: AutomationSortByEnum.AUTHOR },
    { messageKey: "pages.admin.automations.tableHeadings.live" },
    { messageKey: "pages.admin.automations.tableHeadings.runCount" },
    { messageKey: "pages.admin.automations.tableHeadings.actions" }
  ];

  return (
    <>
      {myExpert && (
        <div>
          <Button routerLink="/expert/automations/new">
            <FormattedMessage id="pages.expert.automations.addAutomation" />
          </Button>
        </div>
      )}
      <AutomationFilters filters={filters} onChange={handleFilterChange} />
      <HandleQuery query={getAutomationsQuery}>
        {data => (
          <>
            <Table<AutomationSortByEnum>
              fetchMore={getAutomationsQuery.fetchMore}
              headerKey="pages.admin.automations.tableHeadings"
              headers={tableHeaders}
              pageInfo={data?.automations.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {automations.map(automation => (
                <tr key={automation.id}>
                  <td>
                    <InternalLink
                      linkEventCreateVariables={{}}
                      to={`/${myExpert ? "expert" : "admin"}/automations/${automation.id}`}
                    >
                      {automation.broken && (
                        <>
                          <Icon color="danger" icon="circleExclamation" />
                          <Nbsp />
                        </>
                      )}
                      {automation.title}
                    </InternalLink>
                  </td>
                  <td>
                    <TagList tags={automation.tags} />
                  </td>
                  <td>
                    <DateStamp showWeekday={false} value={automation.createdAt} />
                  </td>
                  <td>
                    <DateStamp showWeekday={false} value={automation.updatedAt} />
                  </td>
                  <td>{automation.author.fullName}</td>
                  <td>
                    {automation.live ? (
                      <Tag color="secondary-500" text={intl.formatMessage({ id: "models.automation.live" })} />
                    ) : (
                      <Tag color="primary-300" text={intl.formatMessage({ id: "models.automation.draft" })} />
                    )}
                  </td>
                  <td>
                    <FormattedNumber value={automation.applicationCount} />
                    {automation.locked && (
                      <>
                        <Nbsp />
                        <Icon icon="lock" />
                      </>
                    )}
                  </td>

                  <td>
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      fill="clear"
                      icon="edit"
                      routerLink={`/${myExpert ? "expert" : "admin"}/automations/${automation.id}/edit`}
                    />
                    <br />
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      fill="clear"
                      icon="trash"
                      onClick={handleAutomationDestroy(automation)}
                    />
                  </td>
                </tr>
              ))}
            </Table>
            <DeleteConfirmationAlert
              messageKey="pages.admin.automations.delete"
              onDelete={handleAutomationDestroyed}
              opener={destroyOpener}
            />
          </>
        )}
      </HandleQuery>
    </>
  );
};

export default AutomationsTable;
